import React, { useEffect, useState, useMemo } from 'react';
import { getQuarterlyIssues } from './quarterlyIssues';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Color palette for multiple fighters
const FIGHTER_COLORS = [
    '#EF476F', '#06D6A0', '#118AB2', '#FFD166', '#8E44AD',
    '#E74C3C', '#27AE60', '#2980B9', '#F39C12', '#9B59B6',
    '#C0392B', '#16A085', '#1A5276', '#D68910', '#6C3483'
];

// Helper function to extract profile ID from profile_link
const getProfileId = (profileLink) => {
    if (!profileLink) return '';
    const parts = profileLink.split('/').filter(p => p); // Filter out empty parts
    return parts.length > 0 ? parts[parts.length - 1] : '';
};

// Helper to display profile ID with # symbol only if ID exists
const displayProfileId = (profileLink) => {
    const id = getProfileId(profileLink);
    return id ? ` #${id}` : '';
};

function FighterEvolution({ gender = 'men' }) {
    const [selectedFighter, setSelectedFighter] = useState('');
    const [filteredFighters, setFilteredFighters] = useState([]); // Array of { name, profile_link }
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedFighters, setSelectedFighters] = useState([]); // Array of { name, profile_link, data, color }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rankType, setRankType] = useState('division'); // 'global' or 'division'
    const [hoveredIssue, setHoveredIssue] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [scaleType, setScaleType] = useState('log'); // 'log' or 'linear'
    const [graphGender, setGraphGender] = useState(gender); // Separate gender state for graph
    const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

    // On mount and when gender changes
    useEffect(() => {
        setSelectedFighter('');
        setSelectedFighters([]);
    }, [gender]);

    // Load default fighters on component mount
    useEffect(() => {
        const defaultFighters = ['Jon Jones', 'Georges St. Pierre', 'Fedor Emelianenko'];
        const loadDefaultFighters = async () => {
            const loaded = [];
            for (let i = 0; i < defaultFighters.length; i++) {
                try {
                    const fighterName = defaultFighters[i];
                    const apiBase = process.env.REACT_APP_API_URL || '';
                    const res = await fetch(
                        `${apiBase}/api/fighter-ranking-history?fighter_name=${encodeURIComponent(fighterName)}&gender=${graphGender}&rank_type=${rankType}`
                    );
                    const result = await res.json();
                    if (Array.isArray(result) && result.length > 0) {
                        const profileLink = result[0].profile_link;
                        loaded.push({
                            name: fighterName,
                            profile_link: profileLink,
                            data: result,
                            color: FIGHTER_COLORS[i % FIGHTER_COLORS.length]
                        });
                    }
                } catch (e) {
                    console.error('Error loading default fighter:', e);
                }
            }
            if (loaded.length > 0) {
                setSelectedFighters(loaded);
            }
        };
        
        loadDefaultFighters();
    }, []);

    // Re-fetch data for all selected fighters when rankType changes
    useEffect(() => {
        if (selectedFighters.length > 0) {
            const refetchAll = async () => {
                const updatedFighters = await Promise.all(
                    selectedFighters.map(async (fighter) => {
                        try {
                            const apiBase = process.env.REACT_APP_API_URL || '';
                            const res = await fetch(
                                `${apiBase}/api/fighter-ranking-history?profile_link=${encodeURIComponent(fighter.profile_link)}&gender=${graphGender}&rank_type=${rankType}`
                            );
                            const result = await res.json();
                            if (Array.isArray(result)) {
                                return { ...fighter, data: result };
                            }
                            return fighter;
                        } catch (e) {
                            console.error('Error refetching fighter data:', e);
                            return fighter;
                        }
                    })
                );
                setSelectedFighters(updatedFighters);
            };
            refetchAll();
        }
    }, [rankType, graphGender]);

    const fetchAvailableFighters = async (searchTerm) => {
        if (!searchTerm || searchTerm.trim() === '') {
            setFilteredFighters([]);
            return;
        }

        setSearchLoading(true);
        try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            const res = await fetch(
                `${apiBase}/api/fighter-names?search=${encodeURIComponent(searchTerm)}&gender=${graphGender}&limit=100`
            );
            const data = await res.json();
            if (Array.isArray(data)) {
                // Filter out already selected fighters (by profile_link)
                const filtered = data.filter(fighter => !selectedFighters.some(f => f.profile_link === fighter.profile_link));
                setFilteredFighters(filtered);
            }
        } catch (e) {
            console.error('Error fetching available fighters:', e);
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle fighter name input change
    const handleFighterInputChange = (e) => {
        const value = e.target.value;
        setSelectedFighter(value);

        if (value.trim() === '') {
            setFilteredFighters([]);
            setShowDropdown(false);
        } else {
            // Fetch fighters from server based on search term
            fetchAvailableFighters(value);
            setShowDropdown(true);
        }
    };

    // Handle fighter selection from dropdown - add to list
    const handleSelectFighter = (fighter) => {
        // Check if already selected
        if (selectedFighters.some(f => f.profile_link === fighter.profile_link)) {
            setSelectedFighter('');
            setShowDropdown(false);
            return;
        }

        // Fetch data for this fighter
        fetchFighterData(fighter);
        setSelectedFighter('');
        setShowDropdown(false);
        setFilteredFighters([]);
    };

    // Fetch evolution data for selected fighter
    const fetchFighterData = async (fighter) => {
        setError('');
        try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            const res = await fetch(
                `${apiBase}/api/fighter-ranking-history?profile_link=${encodeURIComponent(fighter.profile_link)}&gender=${graphGender}&rank_type=${rankType}`
            );
            const result = await res.json();
            if (Array.isArray(result)) {
                if (result.length === 0) {
                    setError(`No ranking data found for ${fighter.name}`);
                } else {
                    // Assign a color to this fighter
                    const colorIndex = selectedFighters.length % FIGHTER_COLORS.length;
                    setSelectedFighters(prev => [...prev, {
                        name: fighter.name,
                        profile_link: fighter.profile_link,
                        data: result,
                        color: FIGHTER_COLORS[colorIndex]
                    }]);
                }
            } else {
                setError(result.error || 'Failed to fetch data');
            }
        } catch (e) {
            setError('Server error: ' + e.message);
        }
    };

    // Remove a fighter from the list
    const removeFighter = (profileLink) => {
        setSelectedFighters(prev => prev.filter(f => f.profile_link !== profileLink));
    };

    // Process data for chart
    const chartData = useMemo(() => {
        if (selectedFighters.length === 0) {
            return { issues: [], issueToDateMap: {}, fightersPoints: {} };
        }

        // Get all unique issues across all fighters
        const issuesSet = new Set();
        selectedFighters.forEach(fighter => {
            fighter.data.forEach(row => issuesSet.add(row.issue));
        });
        const issuesFromData = Array.from(issuesSet).sort((a, b) => a - b);

        if (issuesFromData.length === 0) {
            return { issues: [], issueToDateMap: {}, fightersPoints: {} };
        }

        // Get the latest issue to generate date mappings
        const latestIssue = Math.max(...issuesFromData);
        // Calculate the correct date for the latest issue based on the baseline (issue 144 = Oct 2025)
        const baselineIssue = 144;
        const baselineDate = new Date(2025, 9, 1); // Oct 1, 2025
        const issuesDiff = latestIssue - baselineIssue;
        const correctLatestDate = new Date(baselineDate);
        correctLatestDate.setMonth(correctLatestDate.getMonth() + issuesDiff * 3);
        const latestDateStr = `${String(correctLatestDate.getMonth() + 1).padStart(2, '0')}/${String(correctLatestDate.getDate()).padStart(2, '0')}/${correctLatestDate.getFullYear()}`;
        const allQuarterlyIssues = getQuarterlyIssues(latestIssue, latestDateStr, 200);
        const issueToDateMap = {};
        allQuarterlyIssues.forEach(item => {
            issueToDateMap[item.issue] = item.date;
        });

        // Get min issue from data to know the full range
        const minIssue = Math.min(...issuesFromData);

        // Build complete issues list: all issues from min to max (quarterly issues)
        const issues = allQuarterlyIssues
            .filter(item => item.issue >= minIssue && item.issue <= latestIssue)
            .reverse()
            .map(item => item.issue);

        // Create points for each fighter
        const fightersPoints = {};
        selectedFighters.forEach(fighter => {
            fightersPoints[fighter.profile_link] = fighter.data.map(row => ({
                issue: row.issue,
                rank: row.rank,
                issueIndex: issues.indexOf(row.issue)
            })).filter(p => p.issueIndex !== -1);
        });

        return { issues, issueToDateMap, fightersPoints };
    }, [selectedFighters, rankType]);

    // Chart dimensions - responsive
    const maxSvgWidth = 900;
    const width = maxSvgWidth;
    const height = 520;
    const padding = { top: 40, right: 40, bottom: 60, left: 45 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Scale functions
    const getX = (issueIndex) => {
        if (chartData.issues.length === 0) return 0;
        return padding.left + (issueIndex / (chartData.issues.length - 1 || 1)) * chartWidth;
    };

    const getMaxRank = () => {
        if (!chartData.fightersPoints || Object.keys(chartData.fightersPoints).length === 0) return 100;
        let maxRank = 0;
        Object.values(chartData.fightersPoints).forEach(points => {
            points.forEach(p => {
                if (p.rank > maxRank) maxRank = p.rank;
            });
        });
        // Add 10% padding above and round up
        return Math.ceil(maxRank * 1.1 / 5) * 5;
    };

    const getY = (rank) => {
        const maxRank = getMaxRank();
        if (scaleType === 'log') {
            // Logarithmic scale for Y-axis
            // Avoid log(0) by using max(1, rank)
            const logRank = Math.log(Math.max(1, rank));
            const logMax = Math.log(maxRank);
            return padding.top + (logRank / logMax) * chartHeight;
        } else {
            // Linear scale
            return padding.top + (rank / maxRank) * chartHeight;
        }
    };

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1em' }}>
            {/* Page Header */}
            <div style={{ marginTop: '12em', marginBottom: '2em' }}>
                <p style={{ margin: 0, color: '#aaa', fontSize: '1.1em' }}>
                    Track fighter rankings over time across quarterly issues. Add multiple fighters to compare.
                </p>
            </div>

            {/* Error message */}
            {error && <div style={styles.error}>{error}</div>}

            {/* Main Container - Controls and Chart Side by Side */}
            <div style={{ display: 'flex', gap: '2em', alignItems: 'flex-start' }}>
                {/* Controls Sidebar */}
                <div style={{ flex: '0 0 auto', minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '1.5em', background: '#1a1a1a', padding: '12px', borderRadius: '4px', border: '1px solid #555' }}>
                    {/* Fighter Search Input */}
                    <div>
                        <label style={{ color: '#ccc', fontWeight: 500, display: 'block', marginBottom: '0.5em' }}>
                            Add Fighter
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                value={selectedFighter}
                                onChange={handleFighterInputChange}
                                onFocus={() => selectedFighter.trim() && setShowDropdown(true)}
                                placeholder="Type fighter name..."
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #555',
                                    borderRadius: '4px',
                                    background: '#1a1a1a',
                                    color: '#ddd',
                                    fontSize: '14px',
                                    boxSizing: 'border-box'
                                }}
                            />
                            {showDropdown && (searchLoading ? (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    background: '#222',
                                    border: '1px solid #555',
                                    borderTop: 'none',
                                    borderRadius: '0 0 4px 4px',
                                    padding: '12px',
                                    zIndex: 100,
                                    color: '#999',
                                    fontSize: '14px',
                                    textAlign: 'center'
                                }}>
                                    Searching fighters...
                                </div>
                            ) : filteredFighters.length > 0 ? (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    background: '#222',
                                    border: '1px solid #555',
                                    borderTop: 'none',
                                    borderRadius: '0 0 4px 4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 100
                                }}>
                                    {filteredFighters.map((fighter, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => handleSelectFighter(fighter)}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                borderBottom: idx < filteredFighters.length - 1 ? '1px solid #444' : 'none',
                                                color: '#ccc',
                                                fontSize: '14px',
                                                transition: 'background 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            {fighter.name}<span style={{ color: '#999', fontSize: '12px' }}>{displayProfileId(fighter.profile_link)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null)}
                        </div>
                    </div>

                    {/* Rank Type Toggle */}
                    <div 
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={() => setRankType(rankType === 'global' ? 'division' : 'global')}
                    >
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease'
                            }}
                        >
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '55px', textAlign: 'center' }}>Global Rank</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: rankType === 'global' ? '2px' : '14px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '55px', textAlign: 'center' }}>Division Rank</span>
                        </div>
                    </div>

                    {/* Scale Type Toggle */}
                    <div 
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={() => setScaleType(scaleType === 'log' ? 'linear' : 'log')}
                    >
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease'
                            }}
                        >
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '45px', textAlign: 'center' }}>Log scale</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: scaleType === 'log' ? '2px' : '14px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '45px', textAlign: 'center' }}>Linear scale</span>
                        </div>
                    </div>

                    {/* Graph Gender Toggle */}
                    <div 
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={() => setGraphGender(graphGender === 'men' ? 'women' : 'men')}
                    >
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease'
                            }}
                        >
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '45px', textAlign: 'center' }}>Men</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: graphGender === 'men' ? '2px' : '14px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '45px', textAlign: 'center' }}>Women</span>
                        </div>
                    </div>
                </div>

                {/* Chart and Legend Container */}
                <div style={{ flex: 1 }}>
                    {/* SVG Chart */}
                    {selectedFighters.length > 0 && !loading && (
                        <div style={{ display: 'flex', gap: '8px', minWidth: 0 }}>
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start' }}>
                                <svg 
                                    width={width} 
                                    height={height} 
                                    style={styles.svg}
                                    onMouseMove={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                                    }}
                                >
                            {/* Grid lines for Y-axis */}
                            <g>
                                {(() => {
                                    const maxRank = getMaxRank();
                                    let gridValues = [];
                                    
                                    if (scaleType === 'log') {
                                        // Log scale: show major gridlines at powers of 10
                                        const majorGridValues = [];
                                        for (let power = 0; power <= Math.log10(maxRank); power++) {
                                            const val = Math.pow(10, power);
                                            if (val <= maxRank) {
                                                majorGridValues.push(val);
                                            }
                                        }
                                        gridValues = majorGridValues;
                                    } else {
                                        // Linear scale: show gridlines at regular intervals
                                        const interval = Math.ceil(maxRank / 5);
                                        for (let i = 0; i <= maxRank; i += interval) {
                                            if (i > 0) gridValues.push(i);
                                        }
                                    }
                                    
                                    return gridValues.map((rankValue) => {
                                        let y;
                                        if (scaleType === 'log') {
                                            const logRank = Math.log(rankValue);
                                            const logMax = Math.log(maxRank);
                                            y = padding.top + (logRank / logMax) * chartHeight;
                                        } else {
                                            y = padding.top + (rankValue / maxRank) * chartHeight;
                                        }
                                        
                                        return (
                                            <g key={`grid-${rankValue}`}>
                                                <line
                                                    x1={padding.left}
                                                    y1={y}
                                                    x2={width - padding.right}
                                                    y2={y}
                                                    stroke="#444"
                                                    strokeWidth="1"
                                                    strokeDasharray="2 2"
                                                />
                                                <text
                                                    x={padding.left - 10}
                                                    y={y + 4}
                                                    textAnchor="end"
                                                    fill="#999"
                                                    fontSize="11"
                                                >
                                                    #{Math.round(rankValue)}
                                                </text>
                                            </g>
                                        );
                                    });
                                })()}
                            </g>

                            {/* Y-axis label */}
                            <text
                                x={padding.left - 50}
                                y={height / 2}
                                textAnchor="middle"
                                fill="#fff"
                                fontSize="14"
                                fontWeight="600"
                                transform={`rotate(-90 ${padding.left - 50} ${height / 2})`}
                            >
                                Ranking {scaleType === 'log' ? '(log scale)' : '(linear)'}
                            </text>

                            {/* X-axis labels (January years only, every 5 years) */}
                            <g>
                                {chartData.issues.filter((issue) => {
                                    const dateLabel = chartData.issueToDateMap[issue] || '';
                                    if (!dateLabel.includes('Jan')) return false;
                                    const year = parseInt(dateLabel.split(' ')[1]);
                                    return year % 5 === 0;
                                }).map((issue) => {
                                    const idx = chartData.issues.indexOf(issue);
                                    const dateLabel = chartData.issueToDateMap[issue] || `Issue ${issue}`;
                                    const year = dateLabel.split(' ')[1];
                                    return (
                                        <text
                                            key={`label-${issue}`}
                                            x={getX(idx)}
                                            y={height - padding.bottom + 20}
                                            textAnchor="middle"
                                            fill="#ccc"
                                            fontSize="12"
                                        >
                                            {year}
                                        </text>
                                    );
                                })}
                            </g>

                            {/* X-axis tick marks (January years only, every 5 years) */}
                            <g>
                                {chartData.issues.filter((issue) => {
                                    const dateLabel = chartData.issueToDateMap[issue] || '';
                                    if (!dateLabel.includes('Jan')) return false;
                                    const year = parseInt(dateLabel.split(' ')[1]);
                                    return year % 5 === 0;
                                }).map((issue) => {
                                    const idx = chartData.issues.indexOf(issue);
                                    return (
                                        <line
                                            key={`tick-${issue}`}
                                            x1={getX(idx)}
                                            y1={height - padding.bottom}
                                            x2={getX(idx)}
                                            y2={height - padding.bottom + 5}
                                            stroke="#ccc"
                                            strokeWidth="1.5"
                                        />
                                    );
                                })}
                            </g>

                            {/* Lines for each fighter */}
                            {selectedFighters.map((fighter) => {
                                const points = chartData.fightersPoints[fighter.profile_link] || [];
                                if (points.length === 0) return null;

                                // Build path segments, breaking at non-consecutive issue indices
                                const pathSegments = [];
                                let currentSegment = [];
                                
                                points.forEach((point, i) => {
                                    if (i === 0) {
                                        currentSegment.push(point);
                                    } else {
                                        const prevPoint = points[i - 1];
                                        // Check if consecutive (next index should be exactly prev index + 1)
                                        if (point.issueIndex === prevPoint.issueIndex + 1) {
                                            currentSegment.push(point);
                                        } else {
                                            // Gap detected - save current segment and start new one
                                            if (currentSegment.length > 0) {
                                                pathSegments.push(currentSegment);
                                            }
                                            currentSegment = [point];
                                        }
                                    }
                                });
                                
                                // Don't forget the last segment
                                if (currentSegment.length > 0) {
                                    pathSegments.push(currentSegment);
                                }
                                
                                // Create path data from segments
                                const pathData = pathSegments
                                    .map(segment =>
                                        segment
                                            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(p.issueIndex)} ${getY(p.rank)}`)
                                            .join(' ')
                                    )
                                    .join(' ');

                                return (
                                    <g key={`fighter-${fighter.profile_link}`}>
                                        {/* Line */}
                                        <path
                                            d={pathData}
                                            stroke={fighter.color}
                                            strokeWidth="2"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        
                                        {/* Data points */}
                                        {points.map((point, idx) => {
                                            const isHovered = hoveredIssue === point.issue;
                                            return (
                                                <circle
                                                    key={`point-${fighter.profile_link}-${idx}`}
                                                    cx={getX(point.issueIndex)}
                                                    cy={getY(point.rank)}
                                                    r={isHovered ? "5" : "2"}
                                                    fill={fighter.color}
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={(e) => e.target.setAttribute('r', '5')}
                                                    onMouseLeave={(e) => e.target.setAttribute('r', isHovered ? '5' : '2')}
                                                />
                                            );
                                        })}
                                    </g>
                                );
                            })}

                            {/* Hover zones for each issue */}
                            {chartData.issues.map((issue, idx) => {
                                const x = getX(idx);
                                
                                let zoneX, zoneWidth;
                                if (idx === 0) {
                                    const nextX = idx < chartData.issues.length - 1 ? getX(idx + 1) : width - padding.right;
                                    zoneX = padding.left;
                                    zoneWidth = (x + nextX) / 2 - padding.left;
                                } else if (idx === chartData.issues.length - 1) {
                                    const prevX = getX(idx - 1);
                                    zoneX = (prevX + x) / 2;
                                    zoneWidth = (width - padding.right) - zoneX;
                                } else {
                                    const prevX = getX(idx - 1);
                                    const nextX = getX(idx + 1);
                                    zoneX = (prevX + x) / 2;
                                    zoneWidth = (x + nextX) / 2 - zoneX;
                                }
                                
                                return (
                                    <rect
                                        key={`zone-${issue}`}
                                        x={zoneX}
                                        y={padding.top}
                                        width={zoneWidth}
                                        height={chartHeight}
                                        fill="transparent"
                                        style={{ cursor: 'pointer' }}
                                        onMouseEnter={() => setHoveredIssue(issue)}
                                        onMouseLeave={() => setHoveredIssue(null)}
                                    />
                                );
                            })}

                            {/* Vertical line and tooltip when hovering */}
                            {hoveredIssue !== null && (() => {
                                const hoveredIdx = chartData.issues.indexOf(hoveredIssue);
                                if (hoveredIdx === -1) return null;
                                
                                const dateLabel = chartData.issueToDateMap[hoveredIssue] || `Issue ${hoveredIssue}`;
                                const tooltipWidth = 150;
                                
                                // Calculate height based on number of fighters (with more spacing)
                                let lineCount = 1; // title
                                selectedFighters.forEach(() => lineCount++);
                                const tooltipHeight = 35 + (lineCount * 16); // increased from 20 to 35 for more top spacing
                                
                                // Use cursor position for tooltip, with offset
                                let tooltipX = cursorPos.x + 15;
                                let tooltipY = cursorPos.y - tooltipHeight - 10;
                                
                                // Keep tooltip within bounds
                                if (tooltipX + tooltipWidth > width) {
                                    tooltipX = width - tooltipWidth - 10;
                                }
                                if (tooltipY < 0) {
                                    tooltipY = cursorPos.y + 15;
                                }

                                return (
                                    <g>
                                        {/* Vertical line */}
                                        <line
                                            x1={getX(hoveredIdx)}
                                            y1={padding.top}
                                            x2={getX(hoveredIdx)}
                                            y2={height - padding.bottom}
                                            stroke="#888"
                                            strokeWidth="1.5"
                                            strokeDasharray="4 2"
                                        />

                                        {/* Tooltip background */}
                                        <rect
                                            x={tooltipX}
                                            y={tooltipY}
                                            width={tooltipWidth}
                                            height={tooltipHeight}
                                            fill="#1a1a1a"
                                            stroke="#555"
                                            strokeWidth="1"
                                            rx="8"
                                            ry="8"
                                        />

                                        {/* Tooltip content */}
                                        <text
                                            x={tooltipX + 10}
                                            y={tooltipY + 18}
                                            fill="#fff"
                                            fontSize="12"
                                            fontWeight="700"
                                        >
                                            {dateLabel}
                                        </text>

                                        {selectedFighters.map((fighter, idx) => {
                                            const point = chartData.fightersPoints[fighter.profile_link]?.find(p => p.issue === hoveredIssue);
                                            if (!point) return null;
                                            return (
                                                <g key={`tooltip-${fighter.profile_link}`}>
                                                    <rect
                                                        x={tooltipX + 8}
                                                        y={tooltipY + 45 + idx * 16 - 8}
                                                        width="6"
                                                        height="6"
                                                        fill={fighter.color}
                                                        rx="1"
                                                    />
                                                    <text
                                                        x={tooltipX + 18}
                                                        y={tooltipY + 45 + idx * 16}
                                                        fill="#ccc"
                                                        fontSize="11"
                                                    >
                                                        {fighter.name}
                                                    </text>
                                                    <text
                                                        x={tooltipX + tooltipWidth - 8}
                                                        y={tooltipY + 45 + idx * 16}
                                                        fill="#ccc"
                                                        fontSize="11"
                                                        textAnchor="end"
                                                    >
                                                        {point.rank}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </g>
                                );
                            })()}

                            {/* Axes */}
                            <line
                                x1={padding.left}
                                y1={padding.top}
                                x2={padding.left}
                                y2={height - padding.bottom}
                                stroke="#ccc"
                                strokeWidth="2"
                            />
                            <line
                                x1={padding.left}
                                y1={height - padding.bottom}
                                x2={width - padding.right}
                                y2={height - padding.bottom}
                                stroke="#ccc"
                                strokeWidth="2"
                            />
                        </svg>
                            </div>

                        {/* Legend on the right */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            padding: '16px',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #555',
                            borderRadius: '8px',
                            minWidth: '200px',
                            maxWidth: '220px',
                            height: 'fit-content',
                            overflowY: 'auto'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '13px',
                                fontWeight: '200',
                                color: '#fff',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>Selected Fighters</h3>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                            }}>
                                {selectedFighters.map((fighter) => (
                                    <div key={fighter.profile_link} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px',
                                        padding: '8px 10px',
                                        backgroundColor: '#232323',
                                        border: '1px solid #444',
                                        borderRadius: '4px',
                                        transition: 'border-color 0.2s ease'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            flex: 1,
                                            minWidth: 0
                                        }}>
                                            <div style={{
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: fighter.color,
                                                borderRadius: '2px',
                                                flexShrink: 0
                                            }} />
                                            <span style={{
                                                color: '#ccc',
                                                fontSize: '13px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {fighter.name}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => removeFighter(fighter.profile_link)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#999',
                                                fontSize: '20px',
                                                cursor: 'pointer',
                                                padding: '0',
                                                height: '20px',
                                                width: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'color 0.2s ease',
                                                flexShrink: 0,
                                                lineHeight: 1
                                            }}
                                            onMouseEnter={(e) => e.target.style.color = '#ccc'}
                                            onMouseLeave={(e) => e.target.style.color = '#999'}
                                            title="Remove fighter"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {selectedFighters.length === 0 && (
                                <div style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                                    Add fighters to compare
                                </div>
                            )}
                        </div>
                    </div>
                )}

                    {selectedFighters.length === 0 && !loading && (
                        <div style={styles.placeholder}>
                            Add a fighter and click their name from the dropdown to view their ranking evolution
                        </div>
                    )}

                    {loading && (
                        <div style={styles.loading}>Loading data...</div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    svg: {
        display: 'block'
    },
    error: {
        fontSize: '14px',
        color: '#FF6B6B',
        textAlign: 'center',
        padding: '12px',
        marginBottom: '20px',
        backgroundColor: 'rgba(255, 107, 107, 0.1)',
        borderRadius: '4px',
        border: '1px solid #FF6B6B'
    },
    placeholder: {
        fontSize: '16px',
        color: '#999',
        textAlign: 'center',
        padding: '60px 20px'
    },
    loading: {
        fontSize: '16px',
        color: '#4ECDC4',
        textAlign: 'center',
        padding: '60px 20px'
    }
};

export default FighterEvolution;
